import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { jwtDecode } from 'jwt-decode';
import { environment } from 'environments/environment';
import { MinUserData } from '../user/user.service';

interface DecodedToken{
  user_id: number;
  exp: number;
  email: string
  first_name: string;
  iat: number;
  is_staff: boolean;
  is_superuser: boolean;
  jti: string;
  last_name: string;
  permissions: string[];
  token_type: string;
  username: string;
  password_reset_required: boolean;
  applications: string[];
  market_members: number;
  user_permissions: any[];
}

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
    private readonly JWT_TOKEN = 'JWT_TOKEN';
    public loggedUser?: MinUserData;
    isAuthenticatedSubject = new BehaviorSubject<boolean>(false)
    public lala: Observable<boolean | null>;


    constructor(
        // private userService: UserService,
        private router: Router,
        private http: HttpClient,
    ) {
        this.lala = this.isAuthenticatedSubject.asObservable();

    }

    public get isAuthenticated() {
      return this.isAuthenticatedSubject.value;
    }

    login(username: string, password: string) {
        return this.http.post<any>(`${environment.CORE_URL}/token/`, { username, password }, { withCredentials: true })
        .pipe(
            tap((response: any) =>
             { 
              if (response.detail && response.detail === 'OTP_TOKEN_SENT') {
                return;
              } else {
                this.doLoginUser(JSON.stringify(response))
              }
            }
            )
          );
    }

    private doLoginUser(token: any) {
      const access_token = JSON.parse(token).access;
      const decoded = jwtDecode<DecodedToken>(access_token);
      this.loggedUser = {user_id: decoded?.user_id, email: decoded?.email, username: decoded?.username, is_staff: decoded?.is_staff,
        applications: decoded?.applications, market_members: decoded?.market_members, user_permissions: decoded?.user_permissions
      }
      this.storeJwtToken(token);
      this.isAuthenticatedSubject.next(true);
    }

    private storeJwtToken(jwt: string) {
      localStorage.setItem(this.JWT_TOKEN, jwt);
    }
    

    logout() {
        localStorage.removeItem(this.JWT_TOKEN);
        this.isAuthenticatedSubject.next(false);
        this.loggedUser = null;
        this.router.navigate(['/']);
    }

      
    
    isLoggedIn() {

      return !!localStorage.getItem(this.JWT_TOKEN);
    }
  
    isTokenExpired() {
      const tokens = localStorage.getItem(this.JWT_TOKEN);
      if (!tokens) return true;
      const token = JSON.parse(tokens).access;
      const decoded = jwtDecode(token);
      if (!decoded.exp) return true;
      const expirationDate = decoded.exp * 1000;
      const now = new Date().getTime();
  
      return expirationDate < now;
    }

    isPassResetRequired() {
      let tokens: any = localStorage.getItem(this.JWT_TOKEN);
      if (!tokens) return;
      tokens = JSON.parse(tokens);
      const decoded = jwtDecode<DecodedToken>(tokens.access);
      return decoded?.password_reset_required;
    }

    refreshToken() {
      let tokens: any = localStorage.getItem(this.JWT_TOKEN);
      if (!tokens) return;
      tokens = JSON.parse(tokens);
      let refreshToken = tokens.refresh;
      return this.http.post<any>(`${environment.CORE_URL}/token/refresh/`, { refresh: refreshToken }, { withCredentials: true })
      .pipe(
          tap((accessToken: any) => {
            tokens.access = accessToken.access;
            this.doLoginUser(JSON.stringify(tokens));
          })
      )};
    
    resetPassword(email: string) {
      return this.http.post<any>(`${environment.CORE_URL}/access/user/forgot_password`, { email })
        .pipe(tap((response: any) => localStorage.setItem(email, email)));
    }

    resetPasswordValidation(email: string, reset_token: number, new_password: string) {
      return this.http.post<any>(`${environment.CORE_URL}/access/user/reset_password`, { email, reset_token, new_password })
        .pipe(tap((response: any) => response));
    }
    

    verify(username: string, otp: string) {
      return this.http.post<any>(`${environment.CORE_URL}/token/otp/`, { username, otp })
        .pipe(tap((response: any) => this.doLoginUser(JSON.stringify(response))));
    }

    isStaff(): boolean {
      return this.loggedUser?.is_staff === true;
    }

    // Usuario interno (staff o superuser), segun los claims del access token
    isInternalUser(): boolean {
      const token = this.getJwtToken();
      if (!token) return false;
      try {
        const decoded = jwtDecode<DecodedToken>(token);
        return decoded?.is_staff === true || decoded?.is_superuser === true;
      } catch {
        return false;
      }
    }

    hasPermission(perm: string, app: string): boolean {
      return this.loggedUser?.user_permissions[app].includes(perm);
    }
  

    hasAnyPermission(perms: string[], app: string): boolean {
      return perms.some(perm => this.hasPermission(perm, app));
    }

    hasApplication(app: string): boolean {
      return this.loggedUser?.applications.includes(app);
    }

    canActivate(
      next: ActivatedRouteSnapshot,
      state: RouterStateSnapshot
    ): Observable<boolean> | Promise<boolean|UrlTree> | boolean {
      return this.lala.pipe(
        tap(loggedIn => {
          if (!loggedIn) {
            return this.router.navigate(['/'])
          } else {
            return true;
          }
        })
      );
    }

    canAccessIfStaff(
      next: ActivatedRouteSnapshot,
      state: RouterStateSnapshot
    ): Observable<boolean> | Promise<boolean|UrlTree> | boolean {
      return this.lala.pipe(
        tap(loggedIn => {
          if (!loggedIn) {
            return this.router.navigate(['/'])
          } else {
            if (this.loggedUser?.is_staff) {
              return true;
            } else { 
              return this.router.navigate(['/'])
            }
          }
        })
      );
    }
  
    canActivateChild(  next: ActivatedRouteSnapshot,
      state: RouterStateSnapshot
    ): Observable<boolean> | Promise<boolean|UrlTree> | boolean {
      return this.lala.pipe(
        tap(loggedIn => {
          if (!loggedIn) {
            return this.router.navigate(['/'])
          } else {
            return true;
          }
        })
      );
    }

    getJwtToken(): string | null {
      let tokens: any = localStorage.getItem('JWT_TOKEN');
      if (!tokens) return null;
      const token = JSON.parse(tokens).access;
      return token;
    }
  
}

export const canActivateStaff: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const authService = inject(AuthenticationService);
  if(authService.loggedUser && authService.loggedUser?.is_staff) {
    return true;
  } else {
    inject(Router).navigate(['/']);
    return false
  }
}


export const canActivateMMK: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const authService = inject(AuthenticationService);
  if(authService.loggedUser && authService.loggedUser?.applications.includes('Market Makers')) {
    return true;
  } else {
    inject(Router).navigate(['/']);
    return false
  }
}

export function permissionGuard(route: ActivatedRouteSnapshot): boolean | UrlTree {
  const authService = inject(AuthenticationService);
  const router = inject(Router);

  const requiredPermissions: string[] = route.data['permissions'] || [];
  const requiredSubApp: string = route.data['subapp'] || false;
  const requireStaff: boolean = route.data['requireStaff'] || false;

  if(!authService.loggedUser) {
    inject(Router).navigate(['/']);
    return false;
  }

  const userAppAccess = authService.hasApplication(requiredSubApp);

  const hasPermissions = requiredPermissions.length === 0 || authService.hasAnyPermission(requiredPermissions, requiredSubApp);

  const isStaffOk = !requireStaff || authService.isStaff();

  if(!userAppAccess) {
    return router.createUrlTree(['/']);
  }

  if (hasPermissions && isStaffOk) {
    return true;
  }

  return hasPermissions ? true : router.createUrlTree(['/']);
}
